import {useEffect, useRef} from "react";
import {useIsLoggedIn} from "UserProvider";
import {metaPixelId} from "analytics/analyticsConfig";
import {metaBrowserIdCookie, metaClickIdKeptFor, metaPixelAllowed} from "analytics/metaPixel";
import {
	arrivalDisclosedUnderAnalytics,
	getConsent,
	referringSiteDisclosed,
	storedPurposesVersion,
	subscribeConsent,
} from "consent/consentStore";
import {attributionPayload, submitAdAttribution} from "./AttributionApi";
import {
	captureAdAttribution,
	clearStoredAdAttribution,
	onReferrerReleased,
	rememberLanding,
	storedAdAttribution,
} from "./adAttribution";

/**
 * Advertising attribution's three browser-side moments (ui#256), as a thin React adapter (epic #26):
 *
 *   1. remember this page load's landing — in memory, before any consent, because the visitor may navigate
 *      away from the ad's landing URL before answering the banner;
 *   2. capture what the CURRENT consent allows, on mount and on every later consent change — the click
 *      identifier is only readable once advertising consent exists, and it is still in the URL at that
 *      moment because the banner is answered on the landing page. It captures once more when the sign-in
 *      layer releases this page load's referrer, which the mount capture runs too early to read (api#2395);
 *   3. submit once the visitor is signed in, since the endpoint is account-owned.
 *
 * Mounted at the app root: an ad can land on any public route (a comparison page, a guide, the pricing
 * page), and an attribution that only worked on `/` would lose most of the campaign.
 */
export function useAdAttribution(): void {
	const isLoggedIn = useIsLoggedIn();
	const submitting = useRef(false);

	useEffect(() => {
		// `search` is handed over whole but never kept whole (api#753, api#1244): rememberLanding reduces it
		// to three booleans about the URL and the campaign labels our own ad links carry, and discards the
		// rest — so the click identifier itself never enters memory before advertising consent makes
		// reading it lawful, while the labels, which name our campaign rather than the visitor, do.
		rememberLanding({
			pathname: window.location.pathname,
			referrer: document.referrer,
			search: window.location.search,
			host: window.location.hostname,
		});
		const capture = () => {
			const consent = getConsent();
			captureAdAttribution({
				consent,
				search: window.location.search,
				now: Date.now(),
				referringSiteDisclosed: referringSiteDisclosed(),
				arrivalDisclosedUnderAnalytics: arrivalDisclosedUnderAnalytics(),
				// Meta's click identifier is kept exactly where Meta's pixel may run (kitecorp/transferify#375):
				// the pixel's own rule, asked rather than restated, so the two cannot disagree.
				metaClickIdAllowed: metaPixelAllowed({
					pixelId: metaPixelId(),
					hostname: window.location.hostname,
					pathname: window.location.pathname,
					consent,
					storedVersion: storedPurposesVersion(),
				}),
				metaClickIdKept: metaClickIdKeptFor({pixelId: metaPixelId(), storedVersion: storedPurposesVersion()}),
			});
		};
		capture();
		// Again once the sign-in layer hands this page load's referrer back (api#2395): the capture above
		// ran before it had ruled, so a channel only the referrer proves, and the site behind it, still
		// have to be filled in.
		const unsubscribeConsent = subscribeConsent(capture);
		const unsubscribeRelease = onReferrerReleased(capture);
		return () => {
			unsubscribeConsent();
			unsubscribeRelease();
		};
	}, []);

	useEffect(() => {
		if (!isLoggedIn || submitting.current) {
			return;
		}
		const record = storedAdAttribution(Date.now());
		if (record === null) {
			return;
		}
		submitting.current = true;
		// Meta's browser id is read HERE rather than at capture: Meta's script writes it only once it has
		// loaded, which is after the consent change that triggered the capture.
		submitAdAttribution(attributionPayload(record, metaBrowserIdCookie()))
			.then(() => clearStoredAdAttribution())
			.catch((error: unknown) => {
				// The endpoint is write-once: every 4xx is its final word on this account, so retrying on the
				// next page load could only fail again. An unreachable server is the one case worth keeping
				// the record for.
				if (hasServerResponse(error)) {
					clearStoredAdAttribution();
				}
			})
			.finally(() => {
				submitting.current = false;
			});
	}, [isLoggedIn]);
}

function hasServerResponse(error: unknown): boolean {
	return typeof error === "object" && error !== null && "response" in error
		&& (error as {response?: unknown}).response !== undefined;
}
